import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isAuthFlagEnabled } from '@Core/Auth/authFlags'
import {
    LinkRun,
    LinkRunItem,
    LinkRunOutcome,
} from '@User/Data/DataSource/API/Entity/Identity'
import { useIdentityLinking } from '@User/Hooks/useIdentityLinking/useIdentityLinking'
import { useManagedUsers } from '@User/Hooks/useManagedUsers/useManagedUsers'
import { format } from 'date-fns'
import { useMemo } from 'react'

const TABS: Array<{ value: string; label: string; outcomes: LinkRunOutcome[] }> = [
    { value: 'linkable', label: 'Vinculáveis', outcomes: ['linkable', 'linked'] },
    { value: 'no-match', label: 'Sem correspondência', outcomes: ['no-match'] },
    { value: 'ambiguous', label: 'Ambíguos', outcomes: ['ambiguous'] },
    { value: 'already-linked', label: 'Já vinculados', outcomes: ['already-linked'] },
    { value: 'failed', label: 'Falhas', outcomes: ['failed'] },
    { value: 'offboarded', label: 'Desativados', outcomes: ['offboarded'] },
]

const ERROR_LABELS: Record<string, string> = {
    DUPLICATE_ODHUB_EMAIL: 'E-mail repetido em mais de um cadastro',
    DUPLICATE_DIRECTORY_EMAIL: 'E-mail repetido em mais de uma conta Microsoft',
    MS_ACCOUNT_ALREADY_LINKED: 'Conta Microsoft já vinculada a outro cadastro',
    DIRECTORY_ACCOUNT_DISABLED: 'Conta desativada no diretório',
    FIREBASE_USER_NOT_FOUND: 'Cadastro sem usuário de login',
    USER_ALREADY_LINKED: 'Cadastro já vinculado a outra conta Microsoft',
}

const MODE_LABELS: Record<LinkRun['mode'], string> = {
    'dry-run': 'Prévia',
    execute: 'Vinculação',
    'directory-sync': 'Verificação de desligamentos',
}

const STATUS_LABELS: Record<LinkRun['status'], string> = {
    running: 'Em andamento',
    completed: 'Concluída',
    partial: 'Concluída com falhas',
    failed: 'Falhou',
}

const formatDate = (value?: string | null) =>
    value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '—'

const RunItems = ({ items }: { items: LinkRunItem[] }) => {
    if (!items.length) {
        return <p className="py-6 text-sm text-muted-foreground">Nenhum cadastro nesta lista.</p>
    }
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>E-mail do cadastro</TableHead>
                    <TableHead>Conta Microsoft</TableHead>
                    <TableHead>Observação</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((item) => (
                    <TableRow key={`${item.uid}-${item.outcome}`}>
                        <TableCell>{item.mail || item.uid}</TableCell>
                        <TableCell>{item.msEmail || '—'}</TableCell>
                        <TableCell>
                            {item.error ? ERROR_LABELS[item.error] ?? item.error : ''}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

export const IdentityLinking = () => {
    const { runs, current, running, preview, linkAll, syncDirectory, openRun } =
        useIdentityLinking()
    const { users } = useManagedUsers()
    const bulkLinkEnabled = isAuthFlagEnabled('bulkLinkEnabled')

    const activeUsers = useMemo(
        () => users.filter((user) => !user.firebase?.disabled),
        [users]
    )
    const linkedCount = activeUsers.filter((user) => user.linkStatus === 'linked').length
    const pendingCount = activeUsers.filter((user) => user.linkStatus === 'pending').length
    const linkedShare = activeUsers.length
        ? Math.round((linkedCount / activeUsers.length) * 100)
        : 0

    const linkableCount =
        current?.mode === 'dry-run' ? current.totals.linkable : 0
    const items = current?.items ?? []

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Vínculo com a Microsoft</h1>
                    <p className="text-sm text-muted-foreground">
                        Associa os cadastros existentes às contas do diretório Microsoft pelo
                        e-mail, sem mudar o usuário nem os dados dele.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" disabled={running} onClick={syncDirectory}>
                        Verificar desligamentos
                    </Button>
                    <Button variant="secondary" disabled={running} onClick={preview}>
                        Gerar prévia
                    </Button>
                    <Button
                        disabled={running || !bulkLinkEnabled || !linkableCount}
                        onClick={linkAll}
                        title={
                            bulkLinkEnabled
                                ? undefined
                                : 'Desligado até a validação em homologação (ms_bulk_link_enabled)'
                        }
                    >
                        Vincular {linkableCount} usuários
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                    <CardHeader>
                        <CardDescription>Usuários ativos vinculados</CardDescription>
                        <CardTitle data-testid="linked-share">{linkedShare}%</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                        {linkedCount} de {activeUsers.length} usuários ativos
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardDescription>Ativos pendentes</CardDescription>
                        <CardTitle data-testid="pending-count">{pendingCount}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                        Restringir a senha só depois de ao menos 90% vinculados.
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardDescription>Última execução</CardDescription>
                        <CardTitle className="text-base">
                            {current
                                ? `${MODE_LABELS[current.mode]} · ${STATUS_LABELS[current.status]}`
                                : 'Nenhuma aberta'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                        {current ? formatDate(current.finishedAt ?? current.startedAt) : '—'}
                        {current?.error ? ` · ${current.error}` : ''}
                    </CardContent>
                </Card>
            </div>

            {current && (
                <Tabs defaultValue={current.mode === 'directory-sync' ? 'offboarded' : 'linkable'}>
                    <TabsList className="flex-wrap">
                        {TABS.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value}>
                                {tab.label} (
                                {items.filter((item) => tab.outcomes.includes(item.outcome)).length})
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {TABS.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value}>
                            <RunItems
                                items={items.filter((item) => tab.outcomes.includes(item.outcome))}
                            />
                        </TabsContent>
                    ))}
                </Tabs>
            )}

            <section>
                <h2 className="mb-2 text-lg font-semibold">Histórico</h2>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Início</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Situação</TableHead>
                            <TableHead>Resultado</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {runs.map((run) => (
                            <TableRow
                                key={run._id}
                                className="cursor-pointer"
                                onClick={() => openRun(run._id)}
                            >
                                <TableCell>{formatDate(run.startedAt)}</TableCell>
                                <TableCell>{MODE_LABELS[run.mode]}</TableCell>
                                <TableCell>
                                    <Badge variant={run.status === 'failed' ? 'destructive' : 'secondary'}>
                                        {STATUS_LABELS[run.status]}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {run.mode === 'directory-sync'
                                        ? `${run.totals.offboarded} desativados`
                                        : `${run.totals.linked || run.totals.linkable} vinculáveis/vinculados · ${run.totals.noMatch} sem correspondência · ${run.totals.ambiguous} ambíguos`}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!runs.length && (
                            <TableRow>
                                <TableCell colSpan={4} className="text-sm text-muted-foreground">
                                    Nenhuma execução ainda.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </section>
        </div>
    )
}
